import { alpha, MenuItem, styled } from "@mui/material";
import { useState } from "react";
import Menu, { MenuProps } from "@mui/material/Menu";
import CloudDownload from "@mui/icons-material/CloudDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DownloadOptions, logDownload } from "../Utilities";
import ButtonWithProgress from "./ButtonWithProgress";
import ErrorsString from "../ErrorsString";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
  },
}));

interface Params {
  getDownloadImagesMap: () => Promise<{ [key: string]: DownloadOptions }>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  success: boolean;
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  error: boolean | null;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  isButtonDisabled: boolean;
  setShowAlert: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertMessage: React.Dispatch<React.SetStateAction<string>>;
  workOrderTitle: string;
}

export default function MassiveDownloadDropMenu(params: Params) {
  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  async function downloadImages(
    dynamicDownloadUrls: {
      [key: string]: any;
    },
    buildZip: boolean,
  ): Promise<void> {
    const numberOfUrls = Object.keys(dynamicDownloadUrls).length;
    if (numberOfUrls === 0) {
      params.setShowAlert(true);
      params.setAlertMessage(ErrorsString.noImagesToDownload);
      params.setLoading(false);
      return;
    }
    if (buildZip) {
      downloadImagesAsZip(dynamicDownloadUrls);
    } else {
      downloadImageAsync(dynamicDownloadUrls);
    }
    params.setSuccess(true);
  }

  async function downloadImageAsync(imageUrlsMap: {
    [key: string]: DownloadOptions;
  }) {
    // Iterate each image
    const fileNameMap = {};
    const promises = Object.entries(imageUrlsMap).map(
      async ([url, options]) => {
        const response = await fetch(url);

        if (!response.ok) {
          return;
        }

        const blob = await response.blob();

        // Extract File Name from fetch header
        let fileName = _setFileName(
          response.headers,
          options.fileName,
          fileNameMap,
        );

        // Add image to zip
        saveAs(blob, fileName);

        logDownload(options.uuid, options.template);
      },
    );

    // Wait all images upload
    await Promise.all(promises);

    params.setLoading(false);
  }

  function _setFileName(responseHeaders, imageFileName, fileNameMap) {
    let fileName =
      responseHeaders
        .get("content-disposition")
        ?.split(";")
        .find((n) => n.includes("filename="))
        ?.replace("filename=", "")
        .trim()
        .replace(/"/g, "") || imageFileName;

    if (fileNameMap[fileName]) {
      fileNameMap[fileName] += 1;
      const [name, extension] = fileName.split(".");
      fileName = name + "_" + fileNameMap[fileName] + "." + extension;
    } else {
      fileNameMap[fileName] = 1;
    }

    return fileName;
  }

  /**
   * Sanitize a filename to make it safe for Windows filesystems
   */
  function sanitizeFileName(name: string): string {
    // Remove forbidden Windows characters: \ / : * ? " < > |
    return name
      .replace(/[\\\/:*?"<>|]/g, "_") // replace with underscore
      .replace(/[\u0000-\u001F]/g, "") // remove control characters
      .trim();
  }

  async function downloadImagesAsZip(imageUrlsMap: {
    [key: string]: DownloadOptions;
  }) {
    const zip = new JSZip();

    //Build folder file name
    const workOrderTitle = params.workOrderTitle ?? "";

    const folderName = sanitizeFileName(workOrderTitle);
    const zipName = folderName + ".zip";

    // Create zip folder
    //const imgFolder = zip.folder(folderName);

    // Iterate each image
    const fileNameMap = {};
    const promises = Object.entries(imageUrlsMap).map(
      async ([url, options]) => {
        const response = await fetch(url);

        if (!response.ok) {
          return;
        }

        const blob = await response.blob();

        // Extract File Name from fetch header
        let fileName = _setFileName(
          response.headers,
          options.fileName,
          fileNameMap,
        );
        fileName = sanitizeFileName(fileName);

        // Add image to zip
        zip.file(fileName, blob);

        logDownload(options.uuid, options.template);
      },
    );

    // Wait all images upload
    await Promise.all(promises);

    // generate zip file
    zip.generateAsync({ type: "blob" }).then((zipFile) => {
      // Download ZIP
      saveAs(zipFile, zipName);
    });

    params.setLoading(false);
  }

  function startDownload(buildZip: boolean) {
    params.setLoading(true);
    (async () => {
      const imageUrlsMap = await params.getDownloadImagesMap();
      downloadImages(imageUrlsMap, buildZip);
    })();
  }

  return (
    <div id="actions">
      <ButtonWithProgress
        id="demo-customized-button"
        aria-controls={open ? "demo-customized-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="contained"
        color="primary"
        disableElevation
        loading={params.loading}
        success={params.success}
        error={!!params.error}
        disabled={params.isButtonDisabled}
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
      >
        Download All
      </ButtonWithProgress>
      <StyledMenu
        id="demo-customized-menu"
        MenuListProps={{
          "aria-labelledby": "demo-customized-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem
          onClick={() => startDownload(true)}
          disabled={params.loading}
          disableRipple
        >
          <CloudDownload />
          Create Zip
        </MenuItem>

        <MenuItem
          onClick={() => startDownload(false)}
          disabled={params.loading}
          disableRipple
        >
          <CloudDownload />
          Download Files
        </MenuItem>
      </StyledMenu>
    </div>
  );
}
